import { useEffect, useState } from "react";
import api, { fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { ChangePassword } from "../../components/ChangePassword";
import { PrivacyPanel } from "../../components/PrivacyPanel";
import { useAuth } from "../../AuthContext";
import { toast } from "sonner";

export default function OwnerSettings() {
  const { user, setUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [amount, setAmount] = useState("");
  const [props_, setProps] = useState([]);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    if (user) { setEnabled(!!user.pre_approval_enabled); setAmount(String(user.pre_approval_amount || "")); }
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/roster-options").then((r) => setSubs(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed from user once on load; api/load are stable
  }, [user]);

  const save = async () => {
    try {
      const r = await api.patch("/owner/settings", { pre_approval_enabled: enabled, pre_approval_amount: Number(amount || 0) });
      setUser(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setPreferred = async (pid, sid) => {
    await api.post(`/properties/${pid}/preferred-contractor`, { subcontractor_id: sid });
    toast.success("Saved");
    const r = await api.get("/properties");
    setProps(r.data);
  };

  return (
    <Layout title="Settings">
      <div className="space-y-8">
        <section className="card-flat space-y-4 p-6" data-testid="preapproval-settings">
          <p className="label-caps">Auto-approve extra work</p>
          <p className="text-sm text-muted-foreground">
            Anything outside your maintenance agreement normally needs your OK. Turn this on and extra work quoted at or under your amount goes ahead automatically.
          </p>
          <label className="flex items-center justify-between rounded-md border border-border p-4">
            <span className="text-sm font-semibold">Auto-approve small jobs</span>
            <button data-testid="preapproval-toggle" onClick={() => setEnabled(!enabled)}
              className={`h-8 w-14 rounded-full transition-colors ${enabled ? "bg-primary" : "bg-muted"}`}>
              <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${enabled ? "translate-x-7" : ""}`} />
            </button>
          </label>
          {enabled && (
            <div>
              <p className="mb-1 text-sm font-semibold">Up to</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold">$</span>
                <input data-testid="preapproval-amount" type="number" className="field-input flex-1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="150" />
              </div>
            </div>
          )}
          <button data-testid="preapproval-save" onClick={save} className="btn-primary-big !h-14 !text-base">Save</button>
        </section>

        <section className="card-flat space-y-4 p-6" data-testid="preferred-contractors">
          <p className="label-caps">Preferred contractors</p>
          <p className="text-sm text-muted-foreground">Nominate a contractor you trust for a property. They'll be offered matching jobs first.</p>
          {props_.map((p) => (
            <div key={p.id} className="rounded-md border border-border p-4" data-testid={`pref-prop-${p.id}`}>
              <p className="mb-2 font-semibold">{p.name}</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid={`pref-none-${p.id}`} onClick={() => setPreferred(p.id, null)}
                  className={`h-10 rounded-md border px-3 text-xs font-bold ${!p.preferred_contractor_id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>No preference</button>
                {subs.map((s) => (
                  <button key={s.id} data-testid={`pref-sub-${p.id}-${s.id}`} onClick={() => setPreferred(p.id, s.id)}
                    className={`h-10 rounded-md border px-3 text-xs font-bold ${p.preferred_contractor_id === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{s.name}</button>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="card-flat space-y-4 p-6" data-testid="account-settings">
          <p className="label-caps">My account</p>
          <ChangePassword />
        </section>

        <PrivacyPanel />
      </div>
    </Layout>
  );
}
